import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { PatientDetailPage } from "@/pages/PatientDetailPage";
import PatientProfilePage from "@/pages/PatientProfilePage";
import PrescriptionDetailPage from "@/pages/PrescriptionDetailPage";
import LabReportDetailPage from "@/pages/LabReportDetailPage";
import QRScannerPage from "@/pages/QRScannerPage";
import { UserManagementPage } from "@/pages/UserManagementPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { SystemAnalyticsPage } from "@/pages/SystemAnalyticsPage";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { TrendsPage } from "@/pages/TrendsPage";
import { ComparisonPage } from "@/pages/ComparisonPage";
import SmsSettingsPage from "@/pages/SmsSettingsPage";
import MarketingSmsPage from "@/pages/MarketingSmsPage";
import SmsLogsPage from "@/pages/SmsLogsPage";
import SmsCreditAllocationPage from "@/pages/SmsCreditAllocationPage";
import AdminBulkSmsPage from "@/pages/AdminBulkSmsPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import PatientsPage from "@/pages/PatientsPage";
import ChecklistPage from "@/pages/ChecklistPage";
import ChecklistSettingsPage from "@/pages/ChecklistSettingsPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import DoctorListPage from "@/pages/DoctorListPage";
import DoctorAddPage from "@/pages/DoctorAddPage";
import DoctorEditPage from "@/pages/DoctorEditPage";
import FurtherCheckupsPage from "@/pages/FurtherCheckupsPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import CalendarViewPage from "@/pages/CalendarViewPage";
import HelpSupportPage from "@/pages/HelpSupportPage";
import { Toaster } from "@/components/ui/sonner";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: "#fcfbf9" }}>
        <p style={{ color: "#555555", fontFamily: "Poppins, sans-serif" }}>Loading...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: "#fcfbf9" }}>
        <p style={{ color: "#555555", fontFamily: "Poppins, sans-serif" }}>Loading...</p>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient/:patientId"
            element={
              <ProtectedRoute>
                <PatientProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id"
            element={
              <ProtectedRoute>
                <PatientProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prescriptions/:prescriptionId"
            element={
              <ProtectedRoute>
                <PrescriptionDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:patientId/lab-reports/:reportId"
            element={
              <ProtectedRoute>
                <LabReportDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qr-scanner"
            element={
              <ProtectedRoute>
                <QRScannerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trends/:patientId"
            element={
              <ProtectedRoute>
                <TrendsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compare"
            element={
              <ProtectedRoute>
                <ComparisonPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-analytics"
            element={
              <ProtectedRoute>
                <SystemAnalyticsPage />
              </ProtectedRoute>
            }
          />
          {/* Removed: SMS Settings - SMS footer now in Clinic Settings */}
          <Route
            path="/marketing-sms"
            element={
              <ProtectedRoute>
                <MarketingSmsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sms-logs"
            element={
              <ProtectedRoute>
                <SmsLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sms-credit-allocation"
            element={
              <ProtectedRoute>
                <SmsCreditAllocationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-bulk-sms"
            element={
              <ProtectedRoute>
                <AdminBulkSmsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute>
                <AuditLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients"
            element={
              <ProtectedRoute>
                <PatientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/further-checkups"
            element={
              <ProtectedRoute>
                <FurtherCheckupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <AppointmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarViewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checklist"
            element={
              <ProtectedRoute>
                <ChecklistPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checklist/settings"
            element={
              <ProtectedRoute>
                <ChecklistSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/announcements"
            element={
              <ProtectedRoute>
                <AnnouncementsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctors"
            element={
              <ProtectedRoute>
                <DoctorListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctors/add"
            element={
              <ProtectedRoute>
                <DoctorAddPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctors/edit/:id"
            element={
              <ProtectedRoute>
                <DoctorEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help-support"
            element={
              <ProtectedRoute>
                <HelpSupportPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
